/* eslint-disable no-unused-vars */
import { useState, useEffect, useCallback } from 'react';
import supabase, { orgId } from '../supabaseClient';
import { NM } from '../utils/constants';

export const OTHER_CURRENCIES = ['GBP', 'USD', 'EUR'];

export function getPairLabel(fc, other) {
  return `${fc} : ${other}`;
}

function defaultRates(fc) {
  const r = {};
  OTHER_CURRENCIES.forEach(c => { r[`${fc}_${c}`] = Array(NM).fill(''); });
  return r;
}

export function forwardFill(rates, fc, currentMonthIdx) {
  const cutoff = currentMonthIdx ?? -1;
  const filled = {};
  OTHER_CURRENCIES.forEach(other => {
    const pair = `${fc}_${other}`;
    filled[pair] = [...(rates[pair] || Array(NM).fill(''))];
    if (fc === other) { filled[pair] = Array(NM).fill('1'); return; }
    let last = null;
    for (let i = 0; i < NM; i++) {
      const v = parseFloat(filled[pair][i]);
      if (!isNaN(v) && v > 0) { last = v; }
      else if (last !== null && i > cutoff) { filled[pair][i] = last.toString(); }
    }
  });
  return filled;
}

export default function useFXRates(functionalCurrency, currentMonthIdx) {
  const fc = functionalCurrency || 'USD';
  const [rates, setRates]        = useState(() => defaultRates(fc));
  const [filledRates, setFilled] = useState(() => defaultRates(fc));
  const [loading, setLoading]    = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data } = await supabase.from('fx_rates')
        .select('pair, month_index, rate')
        .eq('org_id', orgId)
        .order('month_index');

      const r = defaultRates(fc);
      (data || []).forEach(row => {
        if (r[row.pair] !== undefined) {
          r[row.pair][row.month_index] = row.rate?.toString() || '';
        }
      });

      OTHER_CURRENCIES.forEach(other => {
        const pair = `${fc}_${other}`;
        if (fc === other) r[pair] = Array(NM).fill('1');
      });

      const filled = forwardFill(r, fc, currentMonthIdx);
      setRates(r);
      setFilled(filled);

      // Write forward-filled future months to Supabase
      const upsertRows = [];
      const cutoff = currentMonthIdx ?? -1;
      OTHER_CURRENCIES.forEach(other => {
        const pair = `${fc}_${other}`;
        if (fc === other) return;
        filled[pair].forEach((v, mi) => {
          if (mi > cutoff) {
            const stored = parseFloat(r[pair][mi]);
            const fill   = parseFloat(v);
            if (isNaN(stored) && !isNaN(fill) && fill > 0) {
              upsertRows.push({ org_id: orgId, pair, month_index: mi, rate: fill });
            }
          }
        });
      });
      if (upsertRows.length > 0) {
        await supabase.from('fx_rates').upsert(upsertRows, { onConflict: 'org_id,pair,month_index' });
      }
      setLoading(false);
    }
    load();
  }, [fc, currentMonthIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRate = useCallback(async (pair, monthIdx, value) => {
    const parts = pair.split('_');
    if (parts[0] === parts[1]) return;

    setRates(prev => {
      const next = { ...prev, [pair]: [...(prev[pair] || Array(NM).fill(''))] };
      next[pair][monthIdx] = value;
      const filled = forwardFill(next, fc, currentMonthIdx);
      setFilled(filled);

      const n = parseFloat(value);
      const upsertRows = [];
      if (!isNaN(n) && n > 0) {
        upsertRows.push({ org_id: orgId, pair, month_index: monthIdx, rate: n });
        const cutoff = currentMonthIdx ?? -1;
        for (let mi = monthIdx + 1; mi < NM; mi++) {
          if (mi > cutoff) {
            const existing = parseFloat(prev[pair][mi]);
            if (isNaN(existing) || existing <= 0) {
              upsertRows.push({ org_id: orgId, pair, month_index: mi, rate: n });
            } else { break; }
          }
        }
      }
      if (upsertRows.length > 0) {
        supabase.from('fx_rates').upsert(upsertRows, { onConflict: 'org_id,pair,month_index' })
          .then(() => {}).catch(e => console.error('[fx save]', e));
      }
      return next;
    });
  }, [fc, currentMonthIdx]);

  const convert = useCallback((usdAmount, targetFC, monthIdx) => {
    if (!targetFC || targetFC === 'USD' || usdAmount === null || usdAmount === undefined) return usdAmount;
    const pair = `${targetFC}_USD`;
    const rate = parseFloat(filledRates[pair]?.[monthIdx]);
    if (!rate || isNaN(rate) || rate === 0) return usdAmount;
    return usdAmount / rate;
  }, [filledRates]);

  const convertFrom = useCallback((amount, fromCurrency, targetFC, monthIdx) => {
    if (!amount || !fromCurrency || !targetFC || fromCurrency === targetFC) return amount;
    if (fromCurrency === 'USD') return convert(amount, targetFC, monthIdx);
    const toUSD = parseFloat(filledRates[`${fromCurrency}_USD`]?.[monthIdx]);
    const usdAmount = toUSD ? amount * toUSD : amount;
    return convert(usdAmount, targetFC, monthIdx);
  }, [filledRates, convert]);

  const convertTotal = useCallback((monthlyUSD, targetFC) => {
    if (!targetFC || targetFC === 'USD') return monthlyUSD.reduce((a, v) => a + (v || 0), 0);
    return monthlyUSD.reduce((a, v, i) => a + (convert(v || 0, targetFC, i) || 0), 0);
  }, [convert]);

  return { rates, filledRates, loading, updateRate, convert, convertFrom, convertTotal };
}
